/**
 * EventFamilyLabelsSection
 *
 * Displays the "For" section in the event modal.
 * Shows ALL family members as toggleable pills, including "Me" and calendar-linked.
 * "Me" is sorted first, then alphabetical.
 */

import {
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import type { EventParticipant } from '@/types/events';
import type { FamilyMember } from '@/types/family-member';
import { useMemo } from 'react';

type EventFamilyLabelsSectionProps = {
  forLabels: EventParticipant[]; // Filtered to role === 'label' only
  familyMembers: FamilyMember[];
  onToggleForMember: (familyMember: FamilyMember) => void;
  isCalendarLinkedToMe: boolean;
  meToggleOff: boolean;
  onToggleMeForEvent: () => void;
  palette: typeof Colors.light;
};

export function EventFamilyLabelsSection({
  forLabels,
  familyMembers,
  onToggleForMember,
  isCalendarLinkedToMe,
  meToggleOff,
  onToggleMeForEvent,
  palette,
}: EventFamilyLabelsSectionProps) {
  // Me member (rendered separately), then remaining sorted alphabetically
  const meMember = useMemo(() => familyMembers.find(fm => fm.isMe), [familyMembers]);

  const nonMeFamilyMembers = useMemo(() => {
    return [...familyMembers]
      .filter(fm => !fm.isMe)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [familyMembers]);

  // Get selected family member IDs
  const selectedFamilyIds = forLabels.map(p => p.familyMemberId!);

  return (
    <View style={styles.container}>
      <View style={styles.iconContainer}>
        <Ionicons name="people-outline" size={18} color={palette.textMuted} />
      </View>
      <View style={styles.content}>
        <Text style={[styles.sectionTitle, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          For
        </Text>
        {(isCalendarLinkedToMe || nonMeFamilyMembers.length > 0) && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.familyPickerContainer}
          >
            {/* Me pill — only on calendars linked to Me */}
            {isCalendarLinkedToMe && meMember && (
              <TouchableOpacity
                style={[
                  styles.familyChip,
                  {
                    backgroundColor: !meToggleOff ? meMember.color : 'transparent',
                    borderColor: meMember.color,
                  },
                ]}
                onPress={onToggleMeForEvent}
                activeOpacity={0.7}
              >
                <View
                  style={[
                    styles.colorDot,
                    { backgroundColor: !meToggleOff ? '#FFFFFF' : meMember.color },
                  ]}
                />
                <Text
                  style={[
                    styles.familyChipText,
                    {
                      color: !meToggleOff ? '#FFFFFF' : palette.text,
                      fontFamily: getBodyFont(),
                    },
                  ]}
                  numberOfLines={1}
                >
                  {meMember.name}
                </Text>
              </TouchableOpacity>
            )}

            {/* Non-Me family member pills */}
            {nonMeFamilyMembers.map(member => {
              const isSelected = selectedFamilyIds.includes(member.id);
              return (
                <TouchableOpacity
                  key={member.id}
                  style={[
                    styles.familyChip,
                    {
                      backgroundColor: isSelected ? member.color : 'transparent',
                      borderColor: member.color,
                    },
                  ]}
                  onPress={() => onToggleForMember(member)}
                  activeOpacity={0.7}
                >
                  <View
                    style={[
                      styles.colorDot,
                      { backgroundColor: isSelected ? '#FFFFFF' : member.color },
                    ]}
                  />
                  <Text
                    style={[
                      styles.familyChipText,
                      {
                        color: isSelected ? '#FFFFFF' : palette.text,
                        fontFamily: getBodyFont(),
                      },
                    ]}
                    numberOfLines={1}
                  >
                    {member.name}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.sm + 2, // 10px
  },
  iconContainer: {
    width: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: Spacing.xs,
  },
  colorDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  familyPickerContainer: {
    flexDirection: 'row',
    gap: Spacing.sm,
    paddingVertical: Spacing.xs,
  },
  familyChip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: Spacing.sm,
    paddingHorizontal: Spacing.md,
    borderRadius: BorderRadius.lg,
    borderWidth: 1.5,
    gap: Spacing.sm,
  },
  familyChipText: {
    fontSize: 14,
    fontWeight: '500',
  },
});
